import { queueDetailRows } from "@/features/kpi/periodFilter";
import { useKpiPeriod } from "@/hooks/useKpiPeriod";
import { useEffect, useState } from "react";

import type { KpiEntry } from "@/api/kpi";
import { useKpiItems, useKpiSnapshot } from "@/api/kpi";
import { SortableTable } from "@/components/SortableTable";
import { ModalShell, ModalTabs } from "@/components/ui";
import { t } from "@/i18n";

import { columnsFor } from "./columns";

interface DestekIadeModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
}

/**
 * `destek_talepleri` için yalnız AÇIK biletler gösterilir — `KpiDetailModal`'daki
 * aynı düzeltmenin (isOpenTicket) yerel kopyası: iki modal AYNI kpi_id için
 * aynı satır kümesini göstermeli (bkz. KpiDetailModal.tsx docstring).
 */
function isOpenTicket(r: KpiEntry): boolean {
  const sc = r.status_code;
  return sc == null || Number(sc) === 1;
}

type Tab = "destek" | "iade";

/**
 * "Cevap bekleyen destek + iade talepleri" birleşik kartının detay modalı —
 * eski ana sayfada tek kart olan `opKpis#destek_iade_talepleri`nin tıklanma
 * hedefi. Backend'de bu id ile bir KPI/loader YOK (yalnız frontend
 * `periodFilter.ts`'in client-side agregatı) — bu yüzden iki sekmede mevcut
 * `destek_talepleri` / `bekleyen_iade` uçları ayrı ayrı çağrılır. Kabuk
 * paylasilan `ModalShell`/`ModalTabs` — Escape/backdrop/body-scroll-kilit
 * orada yonetilir.
 */
export function DestekIadeModal({ open, title, onClose }: DestekIadeModalProps) {
  const { kpiPeriod } = useKpiPeriod();
  const snapshotQuery = useKpiSnapshot();
  const [tab, setTab] = useState<Tab>("destek");
  const destekQuery = useKpiItems(open ? "destek_talepleri" : null);
  const iadeQuery = useKpiItems(open ? "bekleyen_iade" : null);

  useEffect(() => {
    if (open) setTab("destek");
  }, [open]);

  if (!open) return null;

  const activeQuery = tab === "destek" ? destekQuery : iadeQuery;
  const columns = columnsFor(tab === "destek" ? "destek_talepleri" : "bekleyen_iade")!;
  const periodRows = queueDetailRows(tab === "destek" ? "destek_talepleri" : "bekleyen_iade", activeQuery.data?.rows ?? [], snapshotQuery.data?.kpis, kpiPeriod);
  const rows = tab === "destek" ? periodRows.filter(isOpenTicket) : periodRows;

  return (
    <ModalShell
      open
      title={title}
      onClose={onClose}
      size="lg"
      tabs={
        <ModalTabs<Tab>
          value={tab}
          onChange={setTab}
          ariaLabel={title}
          options={[
            { value: "destek", label: t("ozet.destek_iade.tab_destek") },
            { value: "iade", label: t("ozet.destek_iade.tab_iade") },
          ]}
        />
      }
    >
      {activeQuery.isLoading && <p className="text-sm text-muted">{t("common.loading")}</p>}
      {activeQuery.isError && (
        <p role="alert" className="text-sm text-red">
          {t("common.error")}
        </p>
      )}
      {activeQuery.data &&
        (rows.length === 0 ? (
          <p className="text-sm text-muted">{t("table.empty")}</p>
        ) : (
          <SortableTable columns={columns} rows={rows} rowKey={(r) => String(r.ticket_id ?? r.return_id ?? "")} />
        ))}
    </ModalShell>
  );
}
